/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import debounce from 'transmute/debounce';
import { useState, useEffect, useRef, useCallback, createRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectHasDashboardDemoWriteScope } from 'dashboard-lib/public/userInfo/scopes';
import { isReadOnlyDemo } from 'dashboard-lib/public/dashboard/dashboard-logic';
import { getReportId, getReportName, getIsResizable } from 'dashboard-lib/public/widget/widget-schema';
import styled from 'foundations-theming/utils';
import { track as dashboardTracker, EVENTS } from 'dashboard-ui-components/tracking/DashboardUITracker';
import { updateDashboardLayout } from '../../actions/DashboardActions';
import { areLayoutsEqual, getLayoutFromWidgets } from '../../lib/LayoutHelpers';
import { isAcceptanceTestPortal } from '../../lib/isAcceptanceTestPortal';
import WidgetResizeHandle from 'platform-dashboard-ui/components/dashboard/WidgetResizeHandle';
import TransitionWidget from '../widgets/TransitionWidget';
import { getId } from 'dashboard-lib/public/dashboard/dashboard-schema';
import { GridParams } from '../../constants/GridParams';
import DashboardGridItem from './DashboardGridItem';
import { KOALA } from 'HubStyleTokens/colors';
import { CommentableArea, useCollaborationSidebarState } from 'collaboration-sidebar';
import { triggerCSATSurvey } from '../../lib/triggerWootricSurvey';
import { selectHasCSATSurvey } from 'dashboard-lib/public/userInfo/gates';
import { useReportPanelDnD } from '../discovery/useReportPanelDnD';
import { selectHasDashboardPanelAccess } from 'dashboard-lib/public/userInfo/gates';
import { ThemedReactGridLayout } from './ThemedReactGridLayout';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isDocumentSmallScreen = document => document.documentElement.clientWidth < 544;
const StyledDiv = styled.div.withConfig({
  displayName: "WidgetGrid__StyledDiv",
  componentId: "sc-1c1kpja-0"
})(({
  backgroundImage,
  paddingBottom
}) => `
  padding-bottom: ${paddingBottom};
  background-image: ${backgroundImage};
`);
const calcGridUnitWidth = gridWidth => {
  const {
    NUM_COLUMNS,
    WIDGET_MARGIN
  } = GridParams;
  return (gridWidth - WIDGET_MARGIN * (NUM_COLUMNS - 1)) / NUM_COLUMNS;
};
const WidgetGrid = props => {
  const {
    canEditWidgets = false,
    dashboard,
    isPresentation = false,
    widgets
  } = props;
  const {
    isCommentModeActive
  } = useCollaborationSidebarState();
  const {
    NUM_COLUMNS,
    WIDGET_MARGIN
  } = GridParams;
  const isSmallScreen = isDocumentSmallScreen(document);
  const canEditLayout = canEditWidgets && !isSmallScreen && !isCommentModeActive;
  const gridRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [gridWidth, setGridWidth] = useState(0);
  const [stableGridUnitWidth, setStableGridUnitWidth] = useState(0);
  const [shouldPauseAnimation, setShouldPauseAnimation] = useState(false);
  const [displayedLayout, setDisplayedLayout] = useState(getLayoutFromWidgets({
    widgets,
    canEditWidgets: canEditLayout,
    isSmallScreen
  }));
  const [displayedDashboardId, setDisplayedDashboardId] = useState(getId(dashboard));
  const [triggeredSurvey, setTriggeredSurvey] = useState(false);
  const timeout = useRef(null);
  const hasSelectHasCSATSurvey = useSelector(selectHasCSATSurvey);
  const canWriteDemoDashboard = useSelector(selectHasDashboardDemoWriteScope);
  const isReadOnlyDemoDashboard = isReadOnlyDemo(canWriteDemoDashboard, dashboard);
  const hasDashboardPanelAccess = useSelector(selectHasDashboardPanelAccess);
  const {
    dndActive,
    handleDrop,
    optimisticWidgets
  } = useReportPanelDnD({
    widgets,
    gridRef,
    dashboard,
    canEditLayout,
    isSmallScreen,
    displayedLayout,
    setDisplayedLayout
  });
  const displayWidgets = useMemo(() => hasDashboardPanelAccess ? optimisticWidgets : widgets, [hasDashboardPanelAccess, optimisticWidgets, widgets]);
  const gridUnitWidth = calcGridUnitWidth(gridWidth);
  const debouncedConcludeGridResize = useCallback(debounce(250, newGridWidth => {
    setShouldPauseAnimation(false);
    setStableGridUnitWidth(calcGridUnitWidth(newGridWidth));
  }), []);
  useEffect(() => {
    const updateGridWidth = () => {
      if (!gridRef.current || !gridRef.current.offsetWidth) {
        return;
      }
      const newGridWidth = gridRef.current.offsetWidth;
      setShouldPauseAnimation(true);
      setGridWidth(newGridWidth);
      debouncedConcludeGridResize(newGridWidth);
    };
    const resizeObserver = new ResizeObserver(updateGridWidth);
    if (gridRef.current) {
      resizeObserver.observe(gridRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!hasDashboardPanelAccess) {
      setDisplayedLayout(getLayoutFromWidgets({
        widgets,
        canEditWidgets: canEditLayout,
        isSmallScreen
      }));
    }
  }, [canEditLayout, isSmallScreen, widgets, hasDashboardPanelAccess]);
  const handleLayoutChange = newLayout => {
    if (dndActive) {
      return;
    }
    const widgetLayout = getLayoutFromWidgets({
      widgets: hasDashboardPanelAccess ? optimisticWidgets : widgets,
      canEditWidgets: canEditLayout,
      isSmallScreen
    });
    if (isDocumentSmallScreen(document)) {
      return;
    }
    const dashboardId = getId(dashboard);
    if (dashboardId !== displayedDashboardId || displayedLayout.length < newLayout.length) {
      setDisplayedLayout(widgetLayout);
      setDisplayedDashboardId(dashboardId);
    } else if (canEditLayout && dashboardId === displayedDashboardId && !areLayoutsEqual(displayedLayout, newLayout)) {
      updateDashboardLayout(dashboardId, newLayout, isReadOnlyDemoDashboard);
      setDisplayedLayout(newLayout);
      dashboardTracker(EVENTS.updateLayout);
    }
  };
  const getGridBackground = () => {
    const innerGridCells = Array.from(Array(NUM_COLUMNS), (__, i) => `<rect fill='${KOALA}' stroke='#dfe3eb' x='${(gridUnitWidth + WIDGET_MARGIN) * i}' y='0' width='${gridUnitWidth}' height='${gridUnitWidth}' />`).join('');
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${gridWidth}' height='${gridUnitWidth + WIDGET_MARGIN}'>
        ${innerGridCells}
      </svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  };
  const renderDashboardGridItem = widget => /*#__PURE__*/_jsx(DashboardGridItem, {
    dashboard: dashboard,
    gridUnitWidth: stableGridUnitWidth,
    canEditWidgets: canEditWidgets,
    widget: widget,
    isPresentation: isPresentation,
    dashboardTracker: dashboardTracker
  });
  const renderWidget = widget => {
    const reportId = getReportId(widget);
    const reportName = getReportName(widget);
    const isResizable = getIsResizable(widget);
    const ref = /*#__PURE__*/createRef();
    return /*#__PURE__*/_jsx(TransitionWidget, {
      reportId: reportId,
      shouldPauseAnimation: shouldPauseAnimation,
      ref: ref,
      "aria-label": reportName,
      "data-disable-border": isPresentation || isCommentModeActive,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [!isPresentation ? /*#__PURE__*/_jsx(CommentableArea, {
          style: {
            height: '100%'
          },
          "data-onboarding": isAcceptanceTestPortal() ? '' : 'commentable-areas' // Prevent comment tour from rendering on AT portals
          ,
          moduleId: `dashboard-widget-${reportId}`,
          description: {
            translationKey: 'reportName',
            options: {
              reportName
            }
          },
          verticalPositioning: true,
          children: renderDashboardGridItem(widget)
        }) : renderDashboardGridItem(widget), isResizable && /*#__PURE__*/_jsx(WidgetResizeHandle, {
          isEditable: canEditLayout,
          isResizing: isResizing
        })]
      })
    }, reportId);
  };
  const containerPadding = isPresentation ? [20, 20] : [0, 0];
  return !displayWidgets ? null : /*#__PURE__*/_jsx(StyledDiv, {
    ref: gridRef,
    backgroundImage: canEditLayout ? getGridBackground() : 'none',
    paddingBottom: canEditLayout ? `${gridUnitWidth + WIDGET_MARGIN}px` : '0px',
    onMouseEnter: () => {
      triggerCSATSurvey(timeout, hasSelectHasCSATSurvey, triggeredSurvey, setTriggeredSurvey);
    },
    children: /*#__PURE__*/_jsx(ThemedReactGridLayout, {
      isPresentation: isPresentation,
      isEditable: canEditLayout,
      autoSize: true,
      width: gridWidth,
      cols: isSmallScreen ? 1 : NUM_COLUMNS,
      isDraggable: !isSmallScreen,
      containerPadding: containerPadding,
      isResizable: canEditLayout,
      isDroppable: canEditLayout,
      droppingItem: {
        i: '__dropping-elem__',
        w: 3,
        h: 2
      },
      preventCollision: false,
      compactType: "vertical",
      onDragOver: e => {
        e.preventDefault();
        e.stopPropagation();
      },
      onDrop: handleDrop,
      onLayoutChange: handleLayoutChange,
      layout: displayedLayout,
      margin: [WIDGET_MARGIN, WIDGET_MARGIN],
      onResizeStart: () => {
        setIsResizing(true);
      },
      onResizeStop: () => {
        setIsResizing(false);
      },
      rowHeight: isSmallScreen ? 300 : gridUnitWidth,
      useCSSTransforms: true,
      verticalCompact: true,
      draggableCancel: '[data-draggable="false"],  #comment-indicator',
      children: displayWidgets.map(widget => renderWidget(widget)).toArray()
    })
  });
};
WidgetGrid.propTypes = {
  canEditWidgets: PropTypes.bool.isRequired,
  dashboard: PropTypes.instanceOf(ImmutableMap).isRequired,
  isPresentation: PropTypes.bool,
  widgets: PropTypes.object
};
export default WidgetGrid;